import globalCacheManager from './globalCacheManager';

const CACHE_KEY = 'buyer';

class BuyerCache {
  constructor() {
    this.data = null;
    this.pendingPromise = null;
    this.lastFetchTime = null;
    this.isBulkUpdated = false; // Bundle로 업데이트되었는지 여부
  }

  /**
   * 중복 제거 (partner_group과 specification 조합 기준)
   * @param {Array} buyers - 바이어 목록
   * @returns {Array} 중복 제거된 바이어 목록
   */
  removeDuplicates(buyers) {
    if (!buyers || !Array.isArray(buyers)) {
      return [];
    }

    const seen = new Set();
    const uniqueBuyers = [];

    for (const buyer of buyers) {
      const partnerGroup = buyer.partner_group?.trim() || '';
      const specification = buyer.specification?.trim() || '-';
      
      // partner_group이 비어있으면 건너뛰기
      if (!partnerGroup) {
        continue;
      }

      // partner_group과 specification의 조합을 키로 사용
      const key = `${partnerGroup}|||${specification}`;

      // 이미 같은 조합이 있으면 건너뛰기 (첫 번째 것만 유지)
      if (!seen.has(key)) {
        seen.add(key);
        uniqueBuyers.push(buyer);
      }
    }

    return uniqueBuyers;
  }

  /**
   * 바이어 목록 가져오기
   * @param {boolean} forceRefresh - 강제 새로고침 여부
   * @returns {Promise<Array>} 바이어 목록
   */
  async fetchData(forceRefresh = false) {
    // 페이지 새로고침 중이면 Bundle 분배 대기 — 개별 호출 차단
    if (globalCacheManager.isRefreshing) {
      return this.data || [];
    }

    if (!forceRefresh && this.data) return this.data;

    if (this.pendingPromise && !forceRefresh) return this.pendingPromise;

    this.pendingPromise = (async () => {
      try {
        await globalCacheManager.refreshFromBundle(forceRefresh);
        return this.data || [];
      } catch (error) {
        console.error('[BuyerCache] Error refreshing from bundle:', error);
        return this.data || [];
      } finally {
        this.pendingPromise = null;
      }
    })();

    return this.pendingPromise;
  }

  /**
   * 캐시된 바이어 목록 가져오기 (동기)
   * @returns {Array|null} 바이어 목록 또는 null
   */
  getData() {
    return this.data;
  }

  /**
   * 데이터 직접 설정 (Bundle 분배)
   * @param {Array} data - 바이어 목록
   */
  setData(data) {
    // 중복 제거 처리
    const uniqueBuyers = this.removeDuplicates(data || []);
    this.data = uniqueBuyers;
    this.lastFetchTime = Date.now();
    this.isBulkUpdated = true; // Bundle로 업데이트됨
  }

  /**
   * 캐시 무효화
   */
  invalidate() {
    this.data = null;
    this.pendingPromise = null;
    this.lastFetchTime = null;
    this.isBulkUpdated = false;
  }

  /**
   * 마지막 갱신 시간 가져오기
   * @returns {number|null} 마지막 갱신 시간 (타임스탬프)
   */
  getLastFetchTime() {
    return this.lastFetchTime;
  }
}

// 싱글톤 인스턴스
const buyerCache = new BuyerCache();

// 전역 캐시 매니저에 등록
globalCacheManager.register(CACHE_KEY, buyerCache);

/**
 * 바이어 목록 가져오기 (캐시 사용)
 * @param {boolean} forceRefresh - 강제 새로고침 여부
 * @returns {Promise<Array>} 바이어 목록
 */
export const fetchBuyerListCached = async (forceRefresh = false) => {
  return await buyerCache.fetchData(forceRefresh);
};

/**
 * 캐시된 바이어 목록 가져오기 (동기)
 * @returns {Array|null} 바이어 목록 또는 null
 */
export const getBuyerListCached = () => {
  return buyerCache.getData();
};

/**
 * 바이어 캐시 무효화
 */
export const invalidateBuyerCache = () => {
  buyerCache.invalidate();
};

export default buyerCache;

